import { useStytchB2BClient } from '@stytch/react/b2b';
import dayjs from 'dayjs';
import { useEffect, useRef, useCallback, useState } from 'react';

import { resetUser, trackError } from 'utils/analytics';
import { config } from 'utils/config/env';
import { Logger } from 'utils/logger';

import { useAuthStore, useTokenStore } from 'store/index';

// Session timeout configuration
export const SESSION_TIMEOUT = config.VITE_SESSION_TIMEOUT;
export const WARNING_DURATION = config.VITE_WARNING_DURATION;

// Optimized activity events
const ACTIVITY_EVENTS = ['mousedown', 'keypress', 'touchstart'] as const;

// Throttle delay for activity handling to prevent excessive calls
const ACTIVITY_THROTTLE_DELAY = 1000; // 1 second

// Local storage keys/prefixes we want to preserve across logout
const PERSISTENT_LOCAL_STORAGE_KEYS = new Set([
  'close-context-storage', // entity + close period (DatePicker / Entity filter)
  'recon-filters-storage', // reconciliation filters/display options
  'checklist-view-management', // checklist custom views
  'recon-view-management', // reconciliation custom views
]);

const PERSISTENT_LOCAL_STORAGE_PREFIXES = [
  'checklist/views/', // checklist view filters/display options
  'recon/views/', // reconciliation view filters/display options
];

function capturePersistentLocalStorage(): Array<[string, string]> {
  const preservedEntries: Array<[string, string]> = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key) continue;

    const isWhitelisted =
      PERSISTENT_LOCAL_STORAGE_KEYS.has(key) ||
      PERSISTENT_LOCAL_STORAGE_PREFIXES.some((prefix) =>
        key.startsWith(prefix)
      );

    if (!isWhitelisted) continue;

    const value = localStorage.getItem(key);
    if (value !== null) {
      preservedEntries.push([key, value]);
    }
  }
  return preservedEntries;
}

function restorePersistentLocalStorage(entries: Array<[string, string]>) {
  entries.forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });
}

interface SessionManagerConfig {
  timeoutDuration?: number;
  enableActivityMonitoring?: boolean;
  enableWarning?: boolean;
  warningDuration?: number; // Time before timeout to show warning
}

export const useSessionManager = (config: SessionManagerConfig = {}) => {
  let {
    timeoutDuration = SESSION_TIMEOUT,
    enableActivityMonitoring = true,
    enableWarning = true,
    warningDuration = WARNING_DURATION,
  } = config;

  // Validate configuration
  if (enableWarning && warningDuration >= timeoutDuration) {
    Logger.warn(
      'Warning duration must be less than timeout duration. Disabling warning.'
    );

    enableWarning = false;
  }

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const throttleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const lastActivityRef = useRef<number>(0);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const { user } = useAuthStore();
  const { accessToken, clearTokens } = useTokenStore();
  const stytchClient = useStytchB2BClient();

  const performSecureLogout = useCallback(async () => {
    const preservedLocalStorage = capturePersistentLocalStorage();
    try {
      // Logout from server, to revoke stytch token
      await stytchClient.session.revoke();
      // Clear all tokens first
      clearTokens();

      // Clear user data from store
      useAuthStore.getState()?.logout();

      // Clear all stored data while preserving whitelisted entries
      localStorage.clear();
      sessionStorage.clear();
      restorePersistentLocalStorage(preservedLocalStorage);

      // Reset analytics
      resetUser();
    } catch (error) {
      trackError({ message: 'Secure logout error', error });
      Logger.error('Secure logout error:', error);

      // Force redirect even if there's an error
      window.location.href = '/';
    }
  }, [clearTokens, stytchClient]);

  const resetSessionTimer = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
    }

    // Update last activity
    lastActivityRef.current = dayjs().valueOf();

    // Set warning timeout
    if (enableWarning) {
      warningTimeoutRef.current = setTimeout(() => {
        setShowWarningModal(true);
      }, timeoutDuration - warningDuration);
    }

    // Set session timeout
    timeoutRef.current = setTimeout(() => {
      Logger.warn('Session expired due to inactivity');
      performSecureLogout();
    }, timeoutDuration);
  }, [timeoutDuration, enableWarning, warningDuration, performSecureLogout]);

  // Throttled activity handler to prevent excessive calls
  const handleActivity = useCallback(() => {
    if (!enableActivityMonitoring) return;

    // Clear existing throttle timeout
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
    }

    // Set new throttle timeout
    throttleTimeoutRef.current = setTimeout(() => {
      resetSessionTimer();
    }, ACTIVITY_THROTTLE_DELAY);
  }, [enableActivityMonitoring, resetSessionTimer]);

  // Check if session is still valid
  const isSessionValid = useCallback(() => {
    if (!user || !accessToken) {
      return false;
    }

    const timeSinceLastActivity = dayjs().valueOf() - lastActivityRef.current;
    return timeSinceLastActivity < timeoutDuration;
  }, [user, accessToken, timeoutDuration]);

  // Manual logout function
  const logout = useCallback(async () => {
    // Clear all timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
    }

    setShowWarningModal(false);
    await performSecureLogout();
  }, [performSecureLogout]);

  // Handle warning modal actions
  const handleExtendSession = useCallback(() => {
    setShowWarningModal(false);
    resetSessionTimer();
  }, [resetSessionTimer]);

  const handleWarningLogout = useCallback(() => {
    setShowWarningModal(false);
    performSecureLogout();
  }, [performSecureLogout]);

  // Optimized activity monitoring setup
  useEffect(() => {
    if (!user || !accessToken) {
      // Clear any existing timeouts when user is not authenticated
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = undefined;
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = undefined;
      }
      return;
    }

    // Add event listeners for activity monitoring with passive option
    const eventListeners = ACTIVITY_EVENTS.map((event) => {
      const handler = () => handleActivity();
      document.addEventListener(event, handler, { passive: true });
      return { event, handler };
    });

    // Start session timer
    resetSessionTimer();

    // Cleanup function
    return () => {
      // Remove event listeners
      eventListeners.forEach(({ event, handler }) => {
        document.removeEventListener(event, handler);
      });

      // Clear all timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = undefined;
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = undefined;
      }
    };
  }, [user, accessToken, handleActivity, resetSessionTimer]);

  // Check session validity on mount and when dependencies change
  useEffect(() => {
    // Only check session validity if user is authenticated
    if (user && accessToken && !isSessionValid()) {
      performSecureLogout();
    }
  }, [user, accessToken, isSessionValid, performSecureLogout]);

  return {
    logout,
    isSessionValid,
    resetSessionTimer,
    showWarningModal,
    handleExtendSession,
    handleWarningLogout,
    getTimeUntilExpiry: () => {
      const timeSinceLastActivity = dayjs().valueOf() - lastActivityRef.current;
      return Math.max(0, timeoutDuration - timeSinceLastActivity);
    },
  };
};
